"use client";

import React, { useMemo } from "react";

interface SummaryDay {
  date: string; // YYYY-MM-DD
  uptimePercentage: number;
  hadDowntime: boolean;
}

const StatusPage: React.FC = () => {
  // Generate 90 days of data, all showing 100% uptime and no downtime
  const summaryDays = useMemo(() => {
    const result: SummaryDay[] = [];
    const today = new Date();
    for (let i = 89; i >= 0; i--) {
      const date = new Date(
        Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate()
        )
      );
      date.setUTCDate(date.getUTCDate() - i);
      const key = date.toISOString().slice(0, 10);
      result.push({
        date: key,
        uptimePercentage: 100,
        hadDowntime: false,
      });
    }
    return result;
  }, []);

  const overallOperational = true; // Always operational
  const ninetyDayUptime = 100; // Always 100% uptime
  const recentEvents: Array<{
    date: string;
    downStart: string;
    upEnd?: string;
    duration?: number;
  }> = []; // No downtime events

  return (
    <div className="min-h-[calc(100vh-20px)] bg-[#191919] text-white py-8 animate-soft-fade-up">
      <div className="max-w-6xl mx-auto px-4 mt-20">
        {/* Top banner */}
        <div
          className={`rounded-lg p-4 mb-6 ${overallOperational ? "bg-green-500" : "bg-orange-500"} text-white animate-soft-fade-up`}
          style={{ animationDelay: "0.06s" }}
        >
          <h1 className="text-xl font-semibold">
            {overallOperational
              ? "All Systems Operational"
              : "Recent incidents detected in the past 90 days"}
          </h1>
        </div>

        {/* 90-day uptime bar */}
        <div
          className="bg-[#131212] rounded-lg border border-[#333333] p-6 mb-8 animate-soft-fade-up"
          style={{ animationDelay: "0.12s" }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              Uptime over the past 90 days
            </h2>
          </div>
          <div>
            <div
              className="relative flex items-end gap-[2px]"
              style={{ height: 48 }}
            >
              {summaryDays.map((day, idx) => {
                const color = "bg-green-500"; // Always green (operational)
                const tooltipMsg = "System operational - no downtime recorded.";
                return (
                  <div
                    key={day.date + idx}
                    className="group relative flex-1 min-w-[2px]"
                  >
                    <div className={`h-10 rounded-sm ${color}`} />
                    <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-max max-w-xs opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <div className="rounded-md bg-[#191919] border border-[#333333] p-3 text-white">
                        <div className="text-sm font-medium mb-1">
                          {new Date(day.date).toLocaleDateString(undefined, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                        <div className="text-sm text-gray-300">
                          {tooltipMsg}
                        </div>
                      </div>
                      <div className="absolute top-[-6px] left-1/2 -translate-x-1/2 -z-10"></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center text-xs text-gray-400 mt-2">
              <span className="mr-2">90 days ago</span>
              <div className="flex-1 flex items-center">
                <div className="h-px bg-[#333333] flex-1" />
                <span className="mx-2 whitespace-nowrap">
                  {ninetyDayUptime.toFixed(1)} % uptime
                </span>
                <div className="h-px bg-[#333333] flex-1" />
              </div>
              <span className="ml-2">Today</span>
            </div>
          </div>
        </div>

        {/* Recent System Down */}
        <div
          className="bg-[#131212] rounded-lg border border-[#333333] p-6 mb-8 animate-soft-fade-up"
          style={{ animationDelay: "0.18s" }}
        >
          <h3 className="text-lg font-semibold text-white mb-4">
            Recent System Down
          </h3>
          <div className="text-sm text-gray-400">
            No recent downtime events in the past 90 days. All systems
            operational.
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusPage;
